import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Card, CardBody, Badge, Input } from "reactstrap";
import { getClassBookingsByFilter, updateAttendance } from "../../../../../api-handle/classApi";
import "./Booking.scss";

const BookingTable = ({ merchantId, classes }) => {
  const [filterClass, setFilterClass] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [bookingsData, setBookingsData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchBookings = async () => {
    try {
      let queryParams = `?merchantId=${merchantId}&page=${currentPage}&rowsPerPage=${rowsPerPage}`;
      if (filterClass && filterClass !== "all") {
        queryParams += `&filterClass=${filterClass}`;
      }
      if (filterStatus && filterStatus !== "all") {
        queryParams += `&filterStatus=${filterStatus}`;
      }
      if (filterDate) {
        queryParams += `&filterDate=${filterDate}`;
      }

      const response = await getClassBookingsByFilter(queryParams);

      if (response && response.filteredData) {
        setBookingsData(response.filteredData.class || []);
        setTotalRows(response.filteredData.pagination?.totalItems || 0);
        setTotalPages(response.filteredData.pagination?.totalPages || 0);
      } else {
        console.error("Unexpected response structure:", response);
        setBookingsData([]);
        setTotalRows(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setBookingsData([]);
      setTotalRows(0);
      setTotalPages(0);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [filterClass, filterStatus, filterDate, currentPage, rowsPerPage, merchantId]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
  };


  const columns = [
    {
      name: "Class Title",
      selector: (row) => row.class?.title || "N/A",
      sortable: true,
    },
    {
      name: "Student Name",
      selector: (row) => row.user?.name || "N/A",
      sortable: true,
    },
    {
      name: "Booking Date",
      selector: (row) => new Date(row.createdAt).toLocaleDateString() || "N/A",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status || "Pending",
      cell: (row) => {
        const status = row.status?.toLowerCase() || "pending";
        const statusStyles = {
          pending: { background: "#facc15", color: "#92400e" }, // amber
          confirmed: { background: "#4ade80", color: "#166534" }, // green
          cancelled: { background: "#f87171", color: "#7f1d1d" }, // red
          attended: { background: "#38bdf8", color: "#075985" }, // blue
        };

        const style = {
          padding: "4px 10px",
          borderRadius: "999px",
          fontSize: "12px",
          fontWeight: 600,
          textTransform: "capitalize",
          ...statusStyles[status],
        };

        return <span style={style}>{status}</span>;
      },
      sortable: true,
    },
    {
      name: "Payment Status",
      selector: (row) => row.paymentStatus || "Pending",
      cell: (row) => {
        const payment = row.paymentStatus?.toLowerCase() || "pending";
        const paymentStyles = {
          pending: { background: "#fcd34d", color: "#92400e" }, // yellow
          completed: { background: "#60a5fa", color: "#1e3a8a" }, // blue
          failed: { background: "#f87171", color: "#7f1d1d" }, // red
        };

        const style = {
          padding: "4px 10px",
          borderRadius: "999px",
          fontSize: "12px",
          fontWeight: 600,
          textTransform: "capitalize",
          ...paymentStyles[payment],
        };

        return <span style={style}>{payment}</span>;
      },
      sortable: true,
    },
    {
      name: "Attended",
      cell: (row) => {
        const isAttended = row.isAttended === true;

        return (
          <Input
            type="select"
            value={isAttended ? "true" : "false"}
            disabled={isAttended}
            onChange={async (e) => {
              const isAttended = e.target.value === "true";
              await updateAttendance({
                isAttended,
                classBookingId: row._id,
                userId: row.user?._id,
              });
              fetchBookings();
            }}
            style={{
              width: "100px",
              padding: "5px 8px",
              fontSize: "0.9rem",
              borderRadius: "6px",
              backgroundColor: isAttended ? "#e9ecef" : "#fff",
              color: isAttended ? "#6c757d" : "#212529",
              cursor: isAttended ? "not-allowed" : "pointer",
            }}
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </Input>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    }

  ];

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "black",
        color: "white",
        fontWeight: "bold",
        textAlign: "center",
      },
    },
    cells: { style: { textAlign: "center" } },
  };

  return (
    <>
      <div className="booking-filter-container">

        <Input
          type="select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="Confirmed">Confirmed</option>
          <option value="Pending">Pending</option>
        </Input>

        <Input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
        />
      </div >

      <DataTable
        columns={columns}
        data={bookingsData}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        highlightOnHover
        striped
        customStyles={customStyles}
      />
    </>
  );
};

export default BookingTable;